import { getWithAuth, postBlobWithAuth, postWithAuth } from "../helpers/request"

export interface AddUserToPropertyRequest {
    userId: number,
    propertyId: number,
    roleId: number
}

export interface SendWelcomeEmailsRequest {
    corporationId: number,
    secret: string
}

export interface MigrateTicketCategories {
    corporationId: number,
    fromCategoryId: number,
    toCategoryId: number,
    secret: string
}

export interface Category {
    id: number,
    label: string,
}
export interface SendProjectWelcomeEmailsRequest {
    projectId: number
}

export interface SendWelcomeEmailsResponse {
    tenantsMissing: number
}

export interface MigrateTicketCategoriesResponse {
    message: string
}

export interface GenerateToolRegistryTokenResponse {
    token: string,
    message?: string
}

export interface GenerateToolRegistryTokenRequest {
    corporationId: number,
}

export interface SendProjectWelcomeEmailsResponse {
    alreadySent: number,
    failedEmails: any[],
    invitationSent: number,
    residents: number,
    skipLoggedIn: boolean,
    exception?: string,
    message?: string
}

export interface AddUserToPropertyOverviewResponse {
    user: any,
    property: any,
    role: any
}

export interface AddUserToCorporationRequest {
    userId: number,
    corporationId: number,
    roleId: number
}

export interface ChangeCorporationLanguageRequest {
    corporationId: number,
    language: string
}

export interface AddUserToCorporationOverviewResponse {
    corporation: any,
    user: any,
    role: any
}

export interface AddCorporationRequest {
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    address: string,
    zip: string,
    city: string,
    country: string,
    corporation: string
}

export interface AddCorporationResponse {
    user: any,
    password: string,
    exception?: string,
    message?: string
}

export interface GetCorporationProjects {
    corporationId: number
}

export interface GetCorporationTicketCategories {
    corporationId: number
}

export interface RevertMoveOutRequest {
    tenancyPeriodTenantId: number,
    unsetMoveOutDate?: boolean
}

export interface ImportIntegrationFileRequest {
    integrationFileId: number
}

export interface RevertMoveOutRequestOverviewResponse {
    tenant: any,
    unit: any
}

export interface GetCorporationIntegrations {
    corporationId: number,
}

export interface GetCorporationIntegrationFiles {
    corporationId: number,
    integrationTypeId: number
}

export interface ResetPaymentRequest {
    userId: number
}

export interface ReactivateStudentApplicationRequest {
    studentApplicationId: number
}
export interface ReactivateStudentApplicationOverviewResponse {
    applicationId: number,
    corporation: string,
    name: string,
    email: string,
}

export interface GetIntegrationJobLog {
    backofficeReferenceId: string,
}

export interface GetIntegrationJobFiles {
    backofficeReferenceId: string
}

export interface GenerateTenancyReport {
    secret: string,
    autoFixProblems: boolean,
    fromDate?: string,
    toDate?: string,
    corporationIds?: number[],
}

export interface UpdateCorporationPaymentFeeRequest {
    fee: number,
    corporationId: number
}

const TOOLS_ENDPOINTS = {
    ADD_USER_TO_PROPERTY: '/api/system/tools/property-user/add',
    ADD_USER_TO_PROPERTY_OVERVIEW: '/api/system/tools/property-user/add/overview',
    ADD_USER_TO_CORPORATION: '/api/system/tools/corporation-user/add',
    CHANGE_CORPORATION_LANGUAGE: '/api/system/tools/corporation/change-language',
    ADD_USER_TO_CORPORATION_OVERVIEW: '/api/system/tools/corporation-user/add/overview',
    CREATE_CORPORATION: '/api/system/tools/corporation-create',
    IMPORT_KNOWLEDGE_BASE: (projectId: number) => `/api/system/tools/projects/${projectId}/knowledge-base/import`,
    COPY_KNOWLEDGE_BASE: () => `/api/system/tools/knowledge-base/copy`,
    DOWNLOAD_KNOWLEDGE_BASE: () => `/api/system/tools/knowledge-base/download`,
    GET_ALL_CORPORATIONS: '/api/system/tools/corporations',
    GET_ALL_TICKET_CATEGORIES: (corporationId: number) => `/api/system/tools/corporation/${corporationId}/tickets/categories/all`,
    MIGRATE_TICKET_CATEGORIES: '/api/system/tools/ticket/category/migrate',
    SEND_WELCOME_EMAILS: '/api/system/jobs/schedule-welcome-emails',
    SEND_PROJECT_WELCOME_EMAILS: (projectId: number) => `/api/system/tools/welcome-emails/${projectId}`,
    IMPORT_TRANSLATIONS: '/api/system/import-translations',
    GET_CORPORATION_PROJECTS: (corporationId: number) => `/api/system/tools/projects/${corporationId}`,
    REVERT_MOVE_OUT_OVERVIEW: '/api/system/tools/move-out/revert/overview',
    REVERT_MOVE_OUT: '/api/system/tools/move-out/revert',
    RESET_PAYMENT: '/api/system/tools/payment-info/reset',
    REACTIVATE_STUDENT_APPLICATION: '/api/system/tools/student-application/reactivate',
    REACTIVATE_STUDENT_APPLICATION_OVERVIEW: (studentApplicationId: number) =>  `/api/system/tools/student-application/${studentApplicationId}`,
    GET_CORPORATION_INTEGRATIONS: (corporationId: number) => `/api/system/tools/integrations/${corporationId}`,
    GET_CORPORATION_INTEGRATION_FILES: (corporationId: number, integrationTypeId: number) => `/api/system/tools/integrations/${corporationId}/${integrationTypeId}/data`,
    IMPORT_INTEGRATION_FILE: (integrationFileId: number) => `/api/admin/integrations/file/${integrationFileId}/data/import`,
    GET_INTEGRATION_JOB_LOG: (backofficeReferenceId: string) => `/api/admin/integrations/${backofficeReferenceId}/get-log`,
    GET_INTEGRATION_JOB_FILES: (backofficeReferenceId: string) => `/api/admin/integrations/jobs/${backofficeReferenceId}/files`,
    GENERATE_TENANCY_REPORT: () => `/api/system/jobs/tenancy-period-quality`,
    REPORT_SALESFORCE_SYNC_FALSE_POSITIVE: () => `/api/system/tools/integrations/salesforce-sync-false-positive`,
    BACKUP_DATABASE: `/api/system/tools/backup-database`,
    UPDATE_CORPORATION_PAYMENT_FEE: '/api/system/tools/corporation/payments/fee',
    GENERATE_TOOL_REGISTRY_TOKEN: '/api/system/tools/tool-registry-token'
}

export async function addUserToProperty(request: AddUserToPropertyRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.ADD_USER_TO_PROPERTY, { body });
}

export async function addUserToPropertyOverview(request: AddUserToPropertyRequest): Promise<AddUserToPropertyOverviewResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.ADD_USER_TO_PROPERTY_OVERVIEW, { body });
}

export async function addUserToCorporation(request: AddUserToCorporationRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.ADD_USER_TO_CORPORATION, { body });
}

export async function changeCorporationLanguage(request: ChangeCorporationLanguageRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.CHANGE_CORPORATION_LANGUAGE, { body });
}

export async function addUserToCorporationOverview(request: AddUserToCorporationRequest): Promise<AddUserToCorporationOverviewResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.ADD_USER_TO_CORPORATION_OVERVIEW, { body });
}

export async function createCorporation(request: AddCorporationRequest): Promise<AddCorporationResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.CREATE_CORPORATION, { body });
}

export async function getAllCorporations(): Promise<any[]> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_ALL_CORPORATIONS);
}

export async function getAllTicketCategories(request: GetCorporationTicketCategories): Promise<any[]> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_ALL_TICKET_CATEGORIES(request.corporationId));
}

export async function migrateTicketCategories(request: MigrateTicketCategories): Promise<MigrateTicketCategoriesResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.MIGRATE_TICKET_CATEGORIES, { body, retryOnTimeOut: true });
}

export async function sendWelcomeEmails(request: SendWelcomeEmailsRequest): Promise<SendWelcomeEmailsResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.SEND_WELCOME_EMAILS, { body, retryOnTimeOut: true });
}

export async function generateToolRegistryToken(request: GenerateToolRegistryTokenRequest): Promise<GenerateToolRegistryTokenResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.GENERATE_TOOL_REGISTRY_TOKEN, { body, retryOnTimeOut: true });
}

export async function sendProjectWelcomeEmails(request: SendProjectWelcomeEmailsRequest): Promise<SendProjectWelcomeEmailsResponse> {
    return await postWithAuth(TOOLS_ENDPOINTS.SEND_PROJECT_WELCOME_EMAILS(request.projectId));
}

export async function getCorporationProjects(request: GetCorporationProjects): Promise<any> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_CORPORATION_PROJECTS(request.corporationId));
}

export async function revertMoveOutOverview(request: RevertMoveOutRequest): Promise<RevertMoveOutRequestOverviewResponse> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.REVERT_MOVE_OUT_OVERVIEW, { body });
}

export async function revertMoveOut(request: RevertMoveOutRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.REVERT_MOVE_OUT, { body });
}

export async function resetPayment(request: ResetPaymentRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.RESET_PAYMENT, { body });
}

export async function reactivateStudentApplication(request: ReactivateStudentApplicationRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.REACTIVATE_STUDENT_APPLICATION, { body });
}

export async function reactivateStudentApplicationOverview(request: ReactivateStudentApplicationRequest): Promise<ReactivateStudentApplicationOverviewResponse> {
    return await getWithAuth(TOOLS_ENDPOINTS.REACTIVATE_STUDENT_APPLICATION_OVERVIEW(request.studentApplicationId));
}

export async function databaseBackup(): Promise<any> {
    return await postWithAuth(TOOLS_ENDPOINTS.BACKUP_DATABASE);
}

export async function getCorporationIntegrations(request: GetCorporationIntegrations): Promise<any> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_CORPORATION_INTEGRATIONS(request.corporationId));
}

export async function getCorporationIntegrationFiles(request: GetCorporationIntegrationFiles): Promise<any> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_CORPORATION_INTEGRATION_FILES(request.corporationId, request.integrationTypeId));
}

export async function importIntegrationFile(request: ImportIntegrationFileRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.IMPORT_INTEGRATION_FILE(request.integrationFileId), { body });
}

export async function getIntegrationJobLog(request: GetIntegrationJobLog): Promise<any> {
    return await getWithAuth(TOOLS_ENDPOINTS.GET_INTEGRATION_JOB_LOG(request.backofficeReferenceId));
}

export async function getIntegrationJobFiles(request: GetIntegrationJobFiles): Promise<any> {
    const body = JSON.stringify(request);
    const fileName = request.backofficeReferenceId;
    return await postBlobWithAuth(TOOLS_ENDPOINTS.GET_INTEGRATION_JOB_FILES(request.backofficeReferenceId), { body, fileName });
}

export async function generateTenancyReport(request: GenerateTenancyReport): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.GENERATE_TENANCY_REPORT(), { body });
}

export async function updateCorporationPaymentFee(request: UpdateCorporationPaymentFeeRequest): Promise<any> {
    const body = JSON.stringify(request);
    return await postWithAuth(TOOLS_ENDPOINTS.UPDATE_CORPORATION_PAYMENT_FEE, { body });
}